import type { Collector } from './types';
import { prop, safe, signal, unavailable } from './util';

export interface ScreenMetrics {
  screenW: number;
  screenH: number;
  availW: number;
  availH: number;
  innerW: number;
  innerH: number;
  outerW: number;
  outerH: number;
  /** Fullscreen, or running as an installed app: no browser chrome, so screen = viewport legitimately. */
  fullscreen: boolean;
}

/**
 * Heuristics for a spoofed or rounded screen size. Firefox's resistFingerprinting reports the
 * window's content size as the screen size and hides browser chrome; letterboxing rounds the
 * content area to multiples of 200×100.
 */
export function screenSpoofReasons(m: ScreenMetrics): string[] {
  const reasons: string[] = [];
  const screenIsWindow = m.screenW === m.innerW && m.screenH === m.innerH;
  const noChrome = m.availW === m.screenW && m.availH === m.screenH && m.outerW === m.innerW && m.outerH === m.innerH;
  if (screenIsWindow && !m.fullscreen) reasons.push('Screen size equals the page viewport while not in fullscreen');
  if (screenIsWindow && noChrome && !m.fullscreen) reasons.push('No room reported for OS taskbar or browser toolbars');
  if (m.innerW > 0 && m.innerH > 0 && m.innerW % 200 === 0 && m.innerH % 100 === 0)
    reasons.push(`Viewport ${m.innerW}×${m.innerH} is a multiple of 200×100 (letterboxing)`);
  return reasons;
}

/** Any one hint can be coincidence (a 1600×900 window is a multiple of 200×100), so require two. */
export function screenLooksSpoofed(m: ScreenMetrics): boolean {
  return screenSpoofReasons(m).length >= 2;
}

export const screenCollector: Collector = {
  id: 'screen',
  title: 'Screen & display',
  description: 'Your screen size, the space available to windows, pixel density and color depth.',
  async collect() {
    if (typeof screen === 'undefined' || typeof window === 'undefined')
      return [unavailable('screen.size', 'Screen resolution', 'screen is not available')];

    const metrics: ScreenMetrics = {
      screenW: screen.width,
      screenH: screen.height,
      availW: screen.availWidth,
      availH: screen.availHeight,
      innerW: window.innerWidth,
      innerH: window.innerHeight,
      outerW: window.outerWidth,
      outerH: window.outerHeight,
      fullscreen:
        (typeof document !== 'undefined' && !!document.fullscreenElement) ||
        (typeof matchMedia === 'function' && matchMedia('(display-mode: fullscreen), (display-mode: standalone)').matches),
    };
    const reasons = screenSpoofReasons(metrics);
    const spoofed = screenLooksSpoofed(metrics);
    const spoofNote = spoofed ? 'Looks rounded or spoofed, so it is shared by many browsers.' : undefined;

    return Promise.all([
      safe('screen.size', 'Screen resolution', () =>
        signal('screen.size', 'Screen resolution', { width: metrics.screenW, height: metrics.screenH }, {
          display: `${metrics.screenW} × ${metrics.screenH}`,
          ...(spoofed ? { bits: 1, note: spoofNote } : {}),
        }),
      ),
      safe('screen.avail', 'Available screen area', () => {
        const left = prop<number>(screen, 'availLeft');
        const top = prop<number>(screen, 'availTop');
        const value = { width: metrics.availW, height: metrics.availH, left: left ?? null, top: top ?? null };
        const offset = left !== undefined || top !== undefined ? ` at (${left ?? '?'}, ${top ?? '?'})` : '';
        return signal('screen.avail', 'Available screen area', value, {
          display: `${metrics.availW} × ${metrics.availH}${offset}`,
          ...(spoofed ? { bits: 0.5, note: spoofNote } : {}),
        });
      }),
      safe('screen.inner', 'Window viewport (inner)', () =>
        signal('screen.inner', 'Window viewport (inner)', { width: metrics.innerW, height: metrics.innerH }, {
          display: `${metrics.innerW} × ${metrics.innerH}`,
          note: 'Changes as you resize, so it is not part of the stable hash.',
        }),
      ),
      safe('screen.outer', 'Window size (outer)', () =>
        signal('screen.outer', 'Window size (outer)', { width: metrics.outerW, height: metrics.outerH }, {
          display: `${metrics.outerW} × ${metrics.outerH}`,
        }),
      ),
      safe('screen.colorDepth', 'Color depth', () =>
        signal('screen.colorDepth', 'Color depth', { colorDepth: screen.colorDepth, pixelDepth: screen.pixelDepth }, {
          display: `${screen.colorDepth}-bit color · ${screen.pixelDepth}-bit pixels`,
        }),
      ),
      safe('screen.dpr', 'Device pixel ratio', () =>
        signal('screen.dpr', 'Device pixel ratio', window.devicePixelRatio, {
          note: 'Also changes with page zoom.',
          ...(spoofed ? { bits: 0.5 } : {}),
        }),
      ),
      safe('screen.orientation', 'Orientation', () => {
        const o = screen.orientation;
        if (!o) return unavailable('screen.orientation', 'Orientation', 'Screen Orientation API not supported');
        return signal('screen.orientation', 'Orientation', { type: o.type, angle: o.angle }, { display: `${o.type} (${o.angle}°)` });
      }),
      safe('screen.spoofCheck', 'Rounded / spoofed size check', () =>
        signal('screen.spoofCheck', 'Rounded / spoofed size check', { spoofed, reasons }, {
          display: spoofed
            ? `Likely spoofed:\n• ${reasons.join('\n• ')}`
            : reasons.length
              ? `Probably genuine (one weak hint: ${reasons[0]})`
              : 'No signs of rounding or spoofing',
        }),
      ),
    ]);
  },
};
